import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Download, X, Inbox, Edit3, Send, Loader2, ExternalLink, FileText, Image, Archive, Trash2, RotateCcw, Globe, User as UserIcon } from "lucide-react";
import { api, getToken, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STATUS_LABELS, TIER_OPTIONS } from "../lib/brand";
import AppShell from "../components/app/AppShell";

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "in_review", label: "In Review" },
  { key: "placed", label: "Placed" },
  { key: "paid", label: "Paid" },
  { key: "declined", label: "Declined" },
];

function StatusPill({ status, large }) {
  return (
    <span className={`pill status-${status} ${large ? "!px-3 !py-1.5 !text-xs" : ""}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function TierBadge({ tier, large }) {
  const opt = TIER_OPTIONS.find((t) => t.val === tier) || TIER_OPTIONS[0];
  return (
    <span className={`pill border ${large ? "!px-3 !py-1.5 !text-xs" : ""}`} style={{ background: opt.color + "22", borderColor: opt.color, color: opt.color }}>
      {opt.label}
    </span>
  );
}

function StatCard({ label, value, accent, statusKey }) {
  let bg = "#fff", fg = "#1A1A1A", border = "#E8E8E8";
  if (accent) { bg = "#D62828"; fg = "#fff"; border = "#D62828"; }
  else if (statusKey) {
    const map = { new: ["#E3F2FD", "#1565C0"], in_review: ["#FFF3E0", "#E65100"], placed: ["#E8F5E9", "#2E7D32"], paid: ["#E0F7E9", "#1B7A3E"], declined: ["#FFEBEE", "#C62828"] };
    const [b, f] = map[statusKey] || ["#fff", "#1A1A1A"];
    bg = b; fg = f; border = f + "33";
  }
  return (
    <div className="rounded-2xl px-4 py-3 border min-w-[110px]" style={{ background: bg, borderColor: border }}>
      <div className="font-display text-2xl font-black tnum" style={{ color: fg }}>{value}</div>
      <div className="text-[10px] font-extrabold tracking-wider uppercase mt-0.5" style={{ color: accent ? "rgba(255,255,255,0.85)" : fg }}>{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isAgent = user?.role === "agent";
  const isManager = isAdmin || isAgent;
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_review: 0, placed: 0, paid: 0, declined: 0 });
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter === "archived") { params.append("archived_only", "true"); params.append("status_filter", "all"); }
      else { params.append("status_filter", filter); }
      if (search) params.append("search", search);
      if (isAdmin) params.append("scope", scope);
      const statsParams = new URLSearchParams();
      if (isAdmin) statsParams.append("scope", scope);
      const [list, st] = await Promise.all([
        api(`/referrals?${params.toString()}`),
        api(`/referrals/stats?${statsParams.toString()}`),
      ]);
      setItems(list); setStats(st);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter, search, isAdmin, scope]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try { const u = await api(`/referrals/${id}/status`, { method: "PATCH", body: { status } }); setSelected(u); load(); } catch (e) { alert(e.message); }
  };
  const updateTier = async (id, tier) => {
    try { const u = await api(`/referrals/${id}/tier`, { method: "PATCH", body: { tier } }); setSelected(u); load(); } catch (e) { alert(e.message); }
  };
  const toggleArchive = async (id) => {
    try { const u = await api(`/referrals/${id}/archive`, { method: "PATCH" }); setSelected(u); load(); } catch (e) { alert(e.message); }
  };
  const deleteReferral = async (id) => {
    if (!window.confirm("Permanently delete this referral? This cannot be undone.")) return;
    try { await api(`/referrals/${id}`, { method: "DELETE" }); setSelected(null); load(); } catch (e) { alert(e.message); }
  };
  const addNote = async () => {
    if (!selected || !noteText.trim()) return;
    setSavingNote(true);
    try { const u = await api(`/referrals/${selected.id}/notes`, { method: "POST", body: { body: noteText.trim() } }); setSelected(u); setNoteText(""); load(); }
    catch (e) { alert(e.message); }
    finally { setSavingNote(false); }
  };
  const openDocument = async (referralId, docId, name, mime) => {
    try {
      const d = await api(`/referrals/${referralId}/documents/${docId}`);
      const dataUrl = `data:${d.mime || mime || "application/octet-stream"};base64,${d.data_base64}`;
      const w = window.open();
      if (w) {
        if ((mime || d.mime || "").startsWith("image/")) {
          w.document.write(`<title>${d.name}</title><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;height:100vh;"><img src="${dataUrl}" style="max-width:100%;max-height:100%;" /></body>`);
        } else {
          w.document.write(`<title>${d.name}</title><iframe src="${dataUrl}" style="border:0;width:100%;height:100vh;"></iframe>`);
        }
      }
    } catch (e) { alert(e.message || "Could not open document."); }
  };
  const exportCsv = async () => {
    try {
      const tok = getToken();
      const res = await fetch(`${BACKEND_URL}/api/referrals/export.csv`, { headers: { Authorization: `Bearer ${tok}` } });
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl; a.download = `referrals-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(dl);
    } catch (e) { alert(e.message); }
  };

  return (
    <AppShell>
      <div className="px-5 lg:px-10 py-8 max-w-7xl">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-extrabold" style={{ color: "#5A5A5A" }}>Hi, {user?.name?.split(" ")[0]}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black" data-testid="dashboard-title">
              {isAdmin && scope === "mine" ? "My Referrals" : isAdmin ? "All Referrals" : isAgent ? "Team Referrals" : "Your Referrals"}
            </h1>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <button data-testid="export-csv-btn" onClick={exportCsv} className="w-11 h-11 rounded-full grid place-items-center" style={{ background: "#E3F0FB" }}>
                <Download size={18} color="#D62828" />
              </button>
            )}
            {!isManager && (
              <button data-testid="new-referral-fab" onClick={() => navigate("/submit")} className="w-11 h-11 rounded-full grid place-items-center text-white" style={{ background: "#D62828" }}>
                <Plus size={20} />
              </button>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="flex gap-2 mt-4">
            <button data-testid="scope-all" onClick={() => setScope("all")} className={`chip ${scope === "all" ? "chip-active" : "chip-outline"}`}>
              <Globe size={13} /> Master View
            </button>
            <button data-testid="scope-mine" onClick={() => setScope("mine")} className={`chip ${scope === "mine" ? "chip-active" : "chip-outline"}`}>
              <UserIcon size={13} /> My Partners
            </button>
          </div>
        )}

        {isManager && (
          <div className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-full" style={{ background: "#F5F5F5" }}>
            <Search size={16} color="#8A8A8A" />
            <input data-testid="search-input" className="bg-transparent outline-none flex-1 text-sm" placeholder="Search client, partner, or state..." value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <button onClick={() => setSearch("")}><X size={16} /></button>}
          </div>
        )}

        <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
          <StatCard label="Total" value={stats.total} accent />
          <StatCard label="New" value={stats.new} statusKey="new" />
          <StatCard label="In Review" value={stats.in_review} statusKey="in_review" />
          <StatCard label="Placed" value={stats.placed} statusKey="placed" />
          <StatCard label="Paid" value={stats.paid} statusKey="paid" />
          <StatCard label="Declined" value={stats.declined} statusKey="declined" />
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {STATUS_FILTERS.map((f) => (
            <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)} className={`chip ${filter === f.key ? "chip-active" : ""}`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin" color="#D62828" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <Inbox size={48} className="mx-auto" color="#8A8A8A" />
              <h3 className="font-display text-xl font-black mt-3">No referrals yet</h3>
              <p className="text-sm" style={{ color: "#5A5A5A" }}>{isManager ? "Submitted referrals will appear here." : "Tap + to submit your first case."}</p>
              {!isManager && (
                <button data-testid="empty-cta" onClick={() => navigate("/submit")} className="btn-primary mt-5">Submit a Referral</button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <button key={it.id} data-testid={`referral-${it.id}`} onClick={() => setSelected(it)} className={`w-full text-left flex items-center gap-4 bg-white border rounded-2xl p-4 hover:shadow-sm transition ${it.archived ? "opacity-70 border-dashed" : ""}`} style={{ borderColor: "#E8E8E8" }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-extrabold truncate">{it.client_full_name}</span>
                      <TierBadge tier={it.tier} />
                      {it.archived && <span className="pill" style={{ background: "#EEE", color: "#666", border: "1px solid #999" }}>ARCHIVED</span>}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "#5A5A5A" }}>{it.resident_state} • {new Date(it.created_at).toLocaleDateString()}</div>
                    {isManager && (
                      <div className="text-[11px] italic mt-1" style={{ color: "#8A8A8A" }}>From: {it.partner_name} {it.partner_agency ? `(${it.partner_agency})` : ""}</div>
                    )}
                  </div>
                  <StatusPill status={it.status} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <DetailDrawer
          referral={selected}
          isManager={isManager}
          onClose={() => setSelected(null)}
          updateStatus={updateStatus}
          updateTier={updateTier}
          toggleArchive={toggleArchive}
          deleteReferral={deleteReferral}
          openDocument={openDocument}
          noteText={noteText}
          setNoteText={setNoteText}
          addNote={addNote}
          savingNote={savingNote}
          editPath={() => navigate(`/submit?id=${selected.id}`)}
        />
      )}
    </AppShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6">
      <div className="eyebrow mb-2">{title}</div>
      <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E8E8E8" }}>{children}</div>
    </div>
  );
}

function Row({ label, value, multiline }) {
  return (
    <div className={`flex ${multiline ? "flex-col" : "items-center justify-between"} gap-2 py-2.5 border-b last:border-b-0`} style={{ borderColor: "#E8E8E8" }}>
      <div className="text-[11px] uppercase tracking-wider font-extrabold" style={{ color: "#5A5A5A" }}>{label}</div>
      <div className={`text-sm font-semibold ${multiline ? "" : "text-right"}`}>{value || "—"}</div>
    </div>
  );
}

function DetailDrawer({ referral, isManager, onClose, updateStatus, updateTier, toggleArchive, deleteReferral, openDocument, noteText, setNoteText, addNote, savingNote, editPath }) {
  const r = referral;
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="ml-auto relative w-full max-w-2xl h-full bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10" style={{ borderColor: "#E8E8E8" }}>
          <h2 className="font-display text-xl font-black">Case Details</h2>
          <button data-testid="close-detail-btn" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[#F5F5F5]"><X size={22} /></button>
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-2xl font-black">{r.client_full_name}</h3>
              <div className="text-xs mt-1" style={{ color: "#5A5A5A" }}>Submitted {new Date(r.created_at).toLocaleString()}</div>
            </div>
            <StatusPill status={r.status} large />
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            <TierBadge tier={r.tier} large />
            {!isManager && r.status === "new" && (
              <button data-testid="edit-referral-btn" onClick={editPath} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-extrabold" style={{ borderColor: "#D62828", color: "#D62828" }}>
                <Edit3 size={12} /> Edit
              </button>
            )}
          </div>

          <Section title="Stage Timeline">
            {r.timeline?.length ? r.timeline.map((e, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b last:border-b-0" style={{ borderColor: "#E8E8E8" }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: ({ new: "#1565C0", in_review: "#E65100", placed: "#2E7D32", paid: "#1B7A3E", declined: "#C62828" })[e.status] || "#8A8A8A" }} />
                <div className="flex-1">
                  <div className="text-sm font-extrabold">{STATUS_LABELS[e.status] || e.status}</div>
                  <div className="text-xs" style={{ color: "#5A5A5A" }}>{new Date(e.at).toLocaleString()} • {e.by_role === "admin" ? "Admin" : e.by_name}</div>
                </div>
              </div>
            )) : <div className="text-sm" style={{ color: "#5A5A5A" }}>No status changes yet.</div>}
          </Section>

          <Section title="Client">
            <Row label="Phone" value={r.client_phone} />
            <Row label="DOB" value={r.date_of_birth} />
            <Row label="Gender" value={r.gender} />
            <Row label="State / Zip" value={`${r.resident_state} ${r.zip_code || ""}`} />
            <Row label="Household" value={String(r.household_size || "")} />
            <Row label="Coverage For" value={r.who_needs_coverage} />
            <Row label="Income" value={r.estimated_annual_income || "—"} />
          </Section>

          <Section title="Case Background">
            <Row label="Declined Recently" value={r.declined_recently ? "Yes" : "No"} />
            <Row label="Conditions" value={r.major_health_conditions || "—"} multiline />
            <Row label="Prescriptions" value={r.current_prescriptions || "—"} multiline />
            <Row label="Notes" value={r.detailed_case_notes || "—"} multiline />
            <Row label="Budget" value={r.target_monthly_budget || "—"} />
            <Row label="Goals" value={r.coverage_goals || "—"} multiline />
          </Section>

          {r.documents && r.documents.length > 0 && (
            <Section title={`Attachments (${r.documents.length})`}>
              {r.documents.map((d) => (
                <button key={d.id} data-testid={`open-doc-${d.id}`} onClick={() => openDocument(r.id, d.id, d.name, d.mime)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl mt-2 first:mt-0 text-left" style={{ background: "#E3F0FB" }}>
                  {d.mime?.startsWith("image/") ? <Image size={16} color="#D62828" /> : <FileText size={16} color="#D62828" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-extrabold truncate" style={{ color: "#D62828" }}>{d.name}</div>
                    <div className="text-[11px]" style={{ color: "#5A5A5A" }}>{d.size_kb < 1024 ? `${d.size_kb} KB` : `${(d.size_kb / 1024).toFixed(1)} MB`} • Tap to view</div>
                  </div>
                  <ExternalLink size={14} color="#8A8A8A" />
                </button>
              ))}
            </Section>
          )}

          {isManager && (
            <>
              <Section title="Set Tier">
                <p className="text-xs italic mb-2" style={{ color: "#5A5A5A" }}>Tier drives compensation. Pick when reviewing the case.</p>
                <div className="flex flex-wrap gap-2">
                  {TIER_OPTIONS.map((t) => {
                    const active = r.tier === t.val;
                    return (
                      <button key={String(t.val)} data-testid={`set-tier-${t.val ?? "none"}`} onClick={() => updateTier(r.id, t.val)}
                        className={`px-3.5 py-2 rounded-full border-2 text-xs font-extrabold`}
                        style={{ background: active ? t.color : "#F5F5F5", color: active ? "#fff" : "#1A1A1A", borderColor: active ? t.color : "#E8E8E8" }}>
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </Section>
              <Section title="Update Status">
                <div className="flex flex-wrap gap-2">
                  {["new", "in_review", "placed", "paid", "declined"].map((s) => (
                    <button key={s} data-testid={`set-status-${s}`} onClick={() => updateStatus(r.id, s)} className={`pill !text-xs !px-3.5 !py-2 status-${s} border-2`} style={{ borderColor: r.status === s ? undefined : "transparent" }}>
                      {STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>
              </Section>
              <Section title="Manage">
                <div className="flex gap-2 flex-wrap">
                  <button data-testid="archive-btn" onClick={() => toggleArchive(r.id)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border-2 text-xs font-extrabold" style={{ borderColor: "#999", background: "#F5F5F5", color: "#666" }}>
                    {r.archived ? <RotateCcw size={12} /> : <Archive size={12} />} {r.archived ? "Restore" : "Archive"}
                  </button>
                  <button data-testid="delete-btn" onClick={() => deleteReferral(r.id)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border-2 text-xs font-extrabold status-declined" style={{ borderColor: "#C62828" }}>
                    <Trash2 size={12} /> Delete Forever
                  </button>
                </div>
                <p className="text-xs italic mt-3" style={{ color: "#5A5A5A" }}>Archive hides the referral from the active list. Delete permanently removes it.</p>
              </Section>
            </>
          )}

          <Section title={`Notes (${r.notes?.length || 0})`}>
            {r.notes?.length ? r.notes.map((n) => (
              <div key={n.id} className={`p-3 rounded-xl mb-2 ${n.author_role === "admin" ? "border-l-4" : ""}`} style={{ background: n.author_role === "admin" ? "#E3F0FB" : "#F5F5F5", borderColor: "#2E78C5" }}>
                <div className="text-[11px] font-bold" style={{ color: "#5A5A5A" }}>
                  {n.author_role === "admin" ? "Admin" : n.author_name} • {new Date(n.created_at).toLocaleString()}
                </div>
                <div className="text-sm mt-1">{n.body}</div>
              </div>
            )) : <p className="text-xs italic" style={{ color: "#5A5A5A" }}>No notes yet. Be the first.</p>}
            <div className="flex items-end gap-2 mt-2">
              <textarea data-testid="note-input" className="input-field min-h-[44px] resize-none" placeholder="Add a note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} />
              <button data-testid="send-note-btn" onClick={addNote} disabled={!noteText.trim() || savingNote} className="w-11 h-11 rounded-full grid place-items-center text-white shrink-0" style={{ background: "#D62828", opacity: !noteText.trim() || savingNote ? 0.5 : 1 }}>
                {savingNote ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              </button>
            </div>
          </Section>

          <Section title="Partner">
            <Row label="Name" value={r.partner_name} />
            <Row label="Agency" value={r.partner_agency || "—"} />
            <Row label="Email" value={r.partner_email} />
            <Row label="Phone" value={r.partner_phone || "—"} />
          </Section>
        </div>
      </div>
    </div>
  );
}
